import { useEffect, useRef, useState } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { cn } from '@/lib/utils';

/**
 * Linear / NProgress-style 2px progress indicator at the top of the
 * viewport. Animates while a route navigation is pending and fades
 * out the instant it resolves. Replaces the previous full-page
 * `<PageLoadingShell>` `pendingComponent` — gives a clear "something
 * is happening" signal without disrupting the painted content area.
 *
 * Behaviour:
 * - Idle: bar is hidden (opacity 0, off-screen).
 * - Pending starts: bar fades in and animates from 0 → ~80% width
 *   over ~600ms. We never reach 100% during pending — the actual
 *   completion happens when navigation resolves and the bar snaps
 *   to 100% then fades out, mirroring the NProgress pattern.
 * - 200ms grace: navigations that finish under 200ms never show
 *   the bar, mirroring the previous `defaultPendingMs` threshold.
 *   Keeps fast warm-cache navigations completely silent.
 */
export const TopProgressBar = () => {
  const isPending = useRouterState({
    select: (s) => s.status === 'pending' || s.isLoading,
  });

  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(0);
  const showTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const incrementInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  const fadeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const clearAll = () => {
      if (showTimeout.current) clearTimeout(showTimeout.current);
      if (incrementInterval.current) clearInterval(incrementInterval.current);
      if (fadeTimeout.current) clearTimeout(fadeTimeout.current);
      showTimeout.current = null;
      incrementInterval.current = null;
      fadeTimeout.current = null;
    };

    if (isPending) {
      // 200ms grace before the bar appears — fast navigations stay silent.
      showTimeout.current = setTimeout(() => {
        setVisible(true);
        setProgress(10);
        // Trickle towards 80% — speed eases as we approach the cap so the
        // bar never hits 100% on its own. Mimics NProgress's logarithmic curve.
        incrementInterval.current = setInterval(() => {
          setProgress((p) => {
            if (p >= 80) return p;
            const remaining = 80 - p;
            return p + Math.max(0.5, remaining * 0.08);
          });
        }, 200);
      }, 200);
    } else {
      // Resolved: snap to 100, fade, reset.
      clearAll();
      if (visible) {
        setProgress(100);
        fadeTimeout.current = setTimeout(() => {
          setVisible(false);
          setProgress(0);
        }, 200);
      }
    }

    return clearAll;
    // `visible` is read inside the resolved branch but excluded from deps
    // intentionally — including it would re-run on the same pending toggle
    // and reset the trickle timer. Stale-read is fine here (the flag is
    // only used as a "should we trigger the fade-out" gate).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPending]);

  return (
    <div
      role="progressbar"
      aria-hidden={!visible}
      aria-label="Loading"
      className={cn(
        'pointer-events-none fixed top-0 left-0 right-0 z-[100] h-[2px] transition-opacity duration-200',
        visible ? 'opacity-100' : 'opacity-0',
      )}
    >
      <div
        className="bg-primary h-full transition-[width] duration-200 ease-out"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
};
